import { useTranslation } from 'react-i18next';
import { Alert, Paper, Stack, TextField, Typography } from '@mui/material';
import type { LandingContact } from '@dosfilos/domain';
import { contactErrors, type ContactError } from '../../../lib/landing-content';

type Field = keyof LandingContact;

/**
 * Contacto del pie del landing (Configuración → Landing). Lo vacío no se
 * publica; los enlaces deben ser https y el WhatsApp llevar código de país.
 */
export function ContactEditor({
  contact,
  onChange,
}: {
  contact: LandingContact;
  onChange: (next: LandingContact) => void;
}) {
  const { t } = useTranslation('sis');
  const errors = contactErrors(contact);
  const set = (f: Field, v: string) => onChange({ ...contact, [f]: v });
  const errorOf = (f: ContactError) =>
    errors.includes(f)
      ? t(
          f === 'whatsapp'
            ? 'settings.landing.contact.errors.whatsapp'
            : 'settings.landing.contact.errors.url',
        )
      : undefined;

  const field = (f: Field, label: string, helper?: string, placeholder?: string) => {
    const err =
      f === 'address' || f === 'city' || f === 'phone' ? undefined : errorOf(f as ContactError);
    return (
      <TextField
        label={label}
        value={contact[f] ?? ''}
        onChange={(e) => set(f, e.target.value)}
        size="small"
        fullWidth
        error={!!err}
        {...((err ?? helper) ? { helperText: err ?? helper } : {})}
        {...(placeholder ? { placeholder } : {})}
      />
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        display="block"
        mb={1}
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        {t('settings.landing.contact.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('settings.landing.contact.description')}
      </Typography>
      <Stack spacing={2}>
        {errors.length > 0 && (
          <Alert severity="warning">{t('settings.landing.contact.invalid')}</Alert>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {field('address', t('settings.landing.contact.addressLabel'))}
          {field('city', t('settings.landing.contact.cityLabel'))}
        </Stack>
        {field(
          'mapsUrl',
          t('settings.landing.contact.mapsUrlLabel'),
          t('settings.landing.contact.mapsUrlHelper'),
          'https://',
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {field('phone', t('settings.landing.contact.phoneLabel'))}
          {field(
            'whatsapp',
            t('settings.landing.contact.whatsappLabel'),
            t('settings.landing.contact.whatsappHelper'),
          )}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {field(
            'instagram',
            t('settings.landing.contact.instagramLabel'),
            t('settings.landing.contact.urlHelper'),
            'https://instagram.com/…',
          )}
          {field(
            'facebook',
            t('settings.landing.contact.facebookLabel'),
            t('settings.landing.contact.urlHelper'),
            'https://facebook.com/…',
          )}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {field(
            'youtube',
            t('settings.landing.contact.youtubeLabel'),
            t('settings.landing.contact.urlHelper'),
            'https://youtube.com/…',
          )}
          {field(
            'website',
            t('settings.landing.contact.websiteLabel'),
            t('settings.landing.contact.urlHelper'),
            'https://',
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
